import {
  Box,
  Button,
  LinearProgress,
  linearProgressClasses,
  styled,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";

import HelperBotWorkingPopup from "../../components/HelperBotPopup/HelperBotWorkingPopup";
import KillSkillList from "../../components/KillSkillList";

import clockIcon from "../../assets/imgs/crime/clockwise.png";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import config from "../../config/config";
import { CONFIRMATION_DELAY } from "../../constants/const";
import { KillSkillTypeList } from "../../constants/killskill";
import { Messages } from "../../constants/messages";
import { getGameBankAllowance } from "../../helper/contractFunctions/MafiaGameBank";
import { dispatchTxAction } from "../../helper/dispatchTxAction";
import useCurrentTime from "../../hook/useCurrentTime";
import useGameBankBalance from "../../hook/useGameBankBalance";
import useWallet from "../../hook/useWallet";
import {
  approveGameCashAction,
  finishTrain,
  getJailedUntilAction,
  getNextTrainTimeAction,
  getUserHelperBotInfo,
  trainSkillAction,
} from "../../reducers/game.slice";
import { getMyProfile } from "../../reducers/profile.slice";
import { convertDateTimeSimple, toastInfo } from "../../utils/utils";
import useSkillSkillSSE from "./hooks/useKillSkillSSE";

import useStyles from "./index.styles";

const KillSkill = () => {
  const { account } = useWallet();
  const dispatch = useAppDispatch();
  const { classes } = useStyles();
  const currentTime = useCurrentTime();

  const { myProfile } = useAppSelector((state) => state.profile);
  const { nextTrainTime, userKSBotInfo } = useAppSelector(
    (state) => state.game
  );
  const { signMsg, signature } = useAppSelector((state) => state.auth);
  const { balance, updateBalance } = useGameBankBalance(
    account,
    signMsg,
    signature
  );

  const [openBotWorkingPopup, setOpenBotWorkingPopup] = useState(false);

  const BorderLinearProgress = styled(LinearProgress)(
    ({ theme }: { theme: any }) => ({
      height: 10,
      borderRadius: 5,
      [`&.${linearProgressClasses.colorPrimary}`]: {
        background: "rgb(42, 42, 42)",
      },
      [`& .${linearProgressClasses.bar}`]: {
        borderRadius: 5,
        background:
          "linear-gradient(45deg,rgb(220, 236, 102),rgb(129, 218, 99))",
      },
    })
  );

  const renderStatus = () => {
    if (currentTime > nextTrainTime) {
      return "Ready to train! 🎯";
    }

    const timeRemaining = convertDateTimeSimple(nextTrainTime - currentTime);
    return `Next train in: ${timeRemaining}`;
  };

  const handleAttempt = async (trainType: number) => {
    const allowance = await getGameBankAllowance(
      account,
      config.killSkillAddress
    );

    const cost = KillSkillTypeList[trainType].cost;

    if (cost > balance) {
      toastInfo(Messages.KILL_SKILL.TRAIN.INSUFFICIENT_CASH);
      return;
    }

    if (allowance > cost) {
      dispatch(trainSkillAction({ account, trainType }));
    } else {
      dispatchTxAction(
        dispatch,
        approveGameCashAction({
          cashAmount: 10 ** 18,
          account,
          spender: config.killSkillAddress,
        }),
        () => {
          dispatch(trainSkillAction({ account, trainType }));
        },
        CONFIRMATION_DELAY
      );
    }
  };

  // Memoize the callback functions for useCrimeSSE
  const onSuccess = useCallback(
    (xpAmount: number) => {
      toastInfo(Messages.KILL_SKILL.TRAIN.SUCCESS);

      updateBalance(account, signMsg, signature);
      dispatch(getNextTrainTimeAction(account));
      dispatch(finishTrain());

      myProfile.name && dispatch(getMyProfile(myProfile.name));
    },
    [account, dispatch, myProfile.name, signMsg, signature, updateBalance]
  );

  const onFailed = useCallback(() => {
    toastInfo(Messages.KILL_SKILL.TRAIN.FAILED);
    updateBalance(account, signMsg, signature);
    dispatch(getNextTrainTimeAction(account));
    dispatch(finishTrain());
  }, [account, dispatch, signMsg, signature, updateBalance]);

  useEffect(() => {
    dispatch(getNextTrainTimeAction(account));
    dispatch(getJailedUntilAction(account));
    dispatch(getUserHelperBotInfo(account));
  }, [account, dispatch]);

  useSkillSkillSSE(account, onSuccess, onFailed, () => {});

  useEffect(() => {
    if (userKSBotInfo.isRunning) {
      // setOpenBotWorkingPopup(true);
    }
  }, [userKSBotInfo]);

  return (
    <Box className={classes.body}>
      <Box className={classes.container}>
        <Box className={classes.header}>
          <Typography
            fontSize={28}
            color={"white"}
            fontWeight={900}
            fontFamily={"Philosopher"}
          >
            Shooting practise
          </Typography>

          <Box className={classes.progressContainer}>
            <Box
              component={"img"}
              src="/assets/imgs/killskill/target.png"
              width={48}
              height={48}
            ></Box>

            <Typography
              variant="caption"
              color={"white"}
              fontFamily={"Philosopher"}
              fontSize={24}
              fontWeight={900}
              mt={2}
            >
              {(myProfile.ksXp ?? 0) / 10000}%
            </Typography>

            <BorderLinearProgress
              variant="determinate"
              value={(myProfile.ksXp ?? 0) / 10000}
              sx={{
                width: "100%",
                maxWidth: { sm: 240, md: 600 },
              }}
            />

            <Typography
              variant="caption"
              color={"white"}
              fontFamily={"Philosopher"}
              mt={1}
            >
              Your Kill Skill
            </Typography>
          </Box>

          <Button
            startIcon={<Box component="img" src={clockIcon} />}
            variant="contained"
            color="primary"
            size="large"
            className={classes.headerButton}
          >
            {renderStatus()}
          </Button>
        </Box>

        <KillSkillList
          disabled={currentTime < nextTrainTime}
          handleAttempt={handleAttempt}
        />
      </Box>

      <HelperBotWorkingPopup
        openModal={openBotWorkingPopup}
        setOpenModal={setOpenBotWorkingPopup}
      />
    </Box>
  );
};

export default KillSkill;
